import {
  HotelOutlined,
  RestaurantMenu,
  SpeakerGroupOutlined,
} from '@mui/icons-material';
import { Box, Stack } from '@mui/material';

import { selectImpactCalculateTourForCurrentProject } from '@/domain/impact-calculate-tour/impact-calculate-tour.selectors';
import { deleteImpactCo2AnswerTourSequence } from '@/domain/impact-co2/actions/tour/delete-impact-co2-answer-tour-sequence.action';
import { submitImpactCo2AnswerTourSequence } from '@/domain/impact-co2/actions/tour/submit-impact-co2-answer-tour-sequence.action';
import {
  Co2Section,
  ImpactCo2Answer,
} from '@/domain/impact-co2/entities/question.entity';
import { TourSequence } from '@/domain/project-tour-ontour/entities/project-tour-ontour-sequence.entity';
import { selectProjectTourOnTourSequenceById } from '@/domain/project-tour-ontour/project-tour-ontour.selectors';
import { ScoreTag } from '@/ui/components/score/ScoreTag';
import { ScoreText } from '@/ui/components/score-text/ScoreText';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { AccommodationForm } from '@/ui/pages/app/components/quiz-panel/co2/components/forms/AccommodationForm';
import { CateringBeverageForm } from '@/ui/pages/app/components/quiz-panel/co2/components/forms/CateringBeverageForm';
import { CateringMealForm } from '@/ui/pages/app/components/quiz-panel/co2/components/forms/CateringMealForm';
import { EquipmentForm } from '@/ui/pages/app/components/quiz-panel/co2/components/forms/EquipmentForm';
import { QuizFormSeparator } from '@/ui/pages/app/components/quiz-panel/co2/components/QuizFormSeparator';
import { ImpactQuestionContext } from '@/ui/pages/app/components/quiz-panel/ImpactQuestionContext';
import { ColorsScore } from '@/ui/theme/colors-score';

import { TourCard } from '../components/TourCard';

export function TourVariousCost({ sequenceId }: { sequenceId: number }) {
  const sequence = useAppSelector(
    selectProjectTourOnTourSequenceById(sequenceId),
  );

  const calculateTour = useAppSelector(
    selectImpactCalculateTourForCurrentProject,
  );

  const co2 =
    calculateTour?.co2?.ontour.bySequence[sequenceId]?.variousCost?.score || 0;

  const co2Sectors =
    calculateTour?.co2?.ontour.bySequence[sequenceId]?.variousCost?.sectors;

  const co2ByCategories = {
    accommodation: co2Sectors?.artists.categories.accommodation.score || 0,
    catering: co2Sectors?.artists.categories.catering.score || 0,
    equipment: co2Sectors?.artists.categories.equipment.score || 0,
  };

  if (!sequence) {
    return null;
  }

  return (
    <TourCard
      title={translate('ProjectsTour.various-cost')}
      headerRightComponent={
        <ScoreTag topic="co2" value={co2} colors={ColorsScore.none} />
      }
    >
      <Form sequence={sequence} co2ByCategories={co2ByCategories} />
    </TourCard>
  );
}

function Form({
  sequence,
  co2ByCategories,
}: {
  sequence: TourSequence;
  co2ByCategories: {
    accommodation: number;
    catering: number;
    equipment: number;
  };
}) {
  const dispatch = useAppDispatch();

  const sequenceId = sequence.id;

  const onSubmit = async (answer: ImpactCo2Answer) => {
    dispatch(
      submitImpactCo2AnswerTourSequence({
        sequenceId,
        answer,
        submitMode: 'create',
      }),
    );
  };

  const onEdit = async (answer: ImpactCo2Answer) => {
    dispatch(
      submitImpactCo2AnswerTourSequence({
        sequenceId,
        answer,
        submitMode: 'edit',
      }),
    );
  };

  const onDelete = async (answer: ImpactCo2Answer) => {
    dispatch(
      deleteImpactCo2AnswerTourSequence({
        sequenceId,
        answer,
      }),
    );
  };

  const sector = 'ARTISTS';

  return (
    <Stack sx={{ width: '100%' }}>
      <ImpactQuestionContext.Provider
        value={{ panelOpen: true, hideCompleted: false }}
      >
        <Stack sx={{ flexDirection: 'row', gap: 2 }}>
          <Stack sx={{ width: '30rem' }}>
            {/* accommodation */}
            <Box alignSelf={'center'}>
              <ScoreText
                value={`${co2ByCategories.accommodation.toString()} ${translate('Projects.teCO2')}`}
                variant="box"
              />
            </Box>
            <QuizFormSeparator
              icon={<HotelOutlined />}
              label={translate(`Co2.Sections.${Co2Section.ACCOMMODATION}`)}
            />
            <AccommodationForm
              sector={sector}
              onDelete={onDelete}
              onSubmit={onSubmit}
              onEdit={onEdit}
            />
          </Stack>
          <Stack sx={{ width: '30rem' }}>
            {/* catering */}
            <Box alignSelf={'center'}>
              <ScoreText
                value={`${co2ByCategories.catering.toString()} ${translate('Projects.teCO2')}`}
                variant="box"
              />
            </Box>
            <QuizFormSeparator
              icon={<RestaurantMenu />}
              label={translate(`Co2.Sections.${Co2Section.CATERING}`)}
            />
            <CateringMealForm
              sector={sector}
              onDelete={onDelete}
              onSubmit={onSubmit}
              onEdit={onEdit}
            />
            <CateringBeverageForm
              sector={sector}
              onDelete={onDelete}
              onSubmit={onSubmit}
              onEdit={onEdit}
            />
          </Stack>
          <Stack sx={{ width: '30rem' }}>
            {/* equipment */}
            <Box alignSelf={'center'}>
              <ScoreText
                value={`${co2ByCategories.equipment.toString()} ${translate('Projects.teCO2')}`}
                variant="box"
              />
            </Box>
            <QuizFormSeparator
              icon={<SpeakerGroupOutlined />}
              label={translate(`Co2.Sections.${Co2Section.EQUIPMENT}`)}
            />
            <EquipmentForm
              sector={sector}
              onDelete={onDelete}
              onSubmit={onSubmit}
              onEdit={onEdit}
            />
          </Stack>
        </Stack>
      </ImpactQuestionContext.Provider>
    </Stack>
  );
}
